<template>
  <thead class="d-flex TableThead" ref="thead">
    <tr class="header" :style="{width}" ref="header">
      <slot name="table-header"></slot>

      <th v-for="(column, i) in columns" :key="column + i"
        class="cell table-td-cell"
        :class="{
          first: i === 0,
          'ignore-element': dragDisabledOnColumns && dragDisabledOnColumns.includes(columnVariables[i]),
          'drag-handle': enableSortable && (!dragDisabledOnColumns || !dragDisabledOnColumns.includes(columnVariables[i])),
        }"

        :style="sizes && sizes[columnVariables[i]] && {
          'min-width': sizes[columnVariables[i]] + 'px !important',
          'max-width': sizes[columnVariables[i]] + 'px !important',
        }"

        :data-column="column"
        :data-columnvar="columnVariables[i]"
      >
        <div @click="setSort(i)">
          <span v-html="column"></span>
          <SortingOptions v-if="enableSorting"
            :active-sort="columnVariables[i] === sort ? order : null"
          />
        </div>
        <slot :name="`header-${columnVariables[i]}`"></slot>

        <div
          class="bordered ignore-element"

          @pointerdown="evt => pointerDown(evt, i, columnVariables[i])"
        ></div>
      </th>
    </tr>
  </thead>
</template>

<script>

import SortingOptions from "./SortingOptions.vue"

import Sortable from "sortablejs"

export default {
  name: "TheadComponent",
  components: {
    SortingOptions,
  },
  props: {
    value: Object,

    dragDisabledOnColumns: Array,
    sort: {
      type: String,
      required: false,
    },
    order: {
      type: String,
      required: false,
    },
    enableSorting: {
      type: Boolean,
      required: false,
    },
    width: {
      type: [String, Number],
      required: false,
    },
    columns: {
      type: Array,
      required: true,
    },
    columnVariables: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    this.enableSortable && this.mountSortable()

    window.addEventListener("pointermove", this.pagePointerMove, {passive: true})
    window.addEventListener("pointerup", this.pagePointerUp, {passive: true})

    this.fixOverflowAndUndefinedSizes()
    this.$emit("input", this.sizes)
  },
  beforeDestroy() {
    this.destroySortableInstance()

    window.removeEventListener("pointermove", this.pagePointerMove)
    window.removeEventListener("pointerup", this.pagePointerUp)
  },
  data() {
    return {
      mouseX: 0,
      initialX: 0,
      initialClientWidth: 0,
      columnIndex: 0,
      columnResize: null,

      sizes: this.value,
    }
  },
  methods: {
    pagePointerMove(evt) {
      this.mouseX = evt.clientX
    },
    fixOverflowAndUndefinedSizes() {
      let sizes = this.sizes || {}
      if (!Object.keys(sizes).length) return;

      const toRemove = []
      Object.keys(sizes).forEach(key => {
        if (!this.columnVariables.includes(key)) toRemove.push(key)
      })

      const newSizes = {...sizes}
      if (toRemove.length) {
        toRemove.forEach(key => delete newSizes[key])
        this.sizes = newSizes
        sizes = newSizes
      }

      /* if (this.isOverflowing(sizes)) {
        this.removeLastColumnCustomSize(false)

        let lastCustomColumnKey
        this.columnVariables.forEach(column => {
          if (sizes[column]) {
            lastCustomColumnKey = column
          }
        })

        this.sizes = {
          ...sizes,
          [lastCustomColumnKey]: sizes[lastCustomColumnKey] - 10
        }
      } */
    },
    pagePointerUp() {
      this.fixOverflowAndUndefinedSizes()


      this.$emit("input", this.sizes)

      this.initialClientWidth = null
      this.columnIndex = null
      this.initialX = null
      this.columnResize = null
    },
    pointerDown(evt, index, column) {
      this.columnResize = column
      this.initialX = evt.clientX

      const firstChild = this.$refs.header.children[0]
      if (firstChild && !firstChild.classList.contains("table-td-cell")) index++

      this.columnIndex = index
      this.initialClientWidth = this.$refs.header.children[index].clientWidth
    },

    setSort(index) {
      const newSort = this.columnVariables[index]
      const order = this.order

      this.$emit("set-sort", newSort)
      this.$emit("set-order", (
        this.sort !== newSort ? "asc" : this.getReverseOrder(order)
      ))
    },
    getReverseOrder(order) {
      return order === "asc" ? "desc" : "asc"
    },
    isOverflowing(sizes) {
      const children = [...this.$refs.header.children]
      const numberOfCustomSizes = Object.keys(sizes).length

      const totalWidth = children.reduce((tot, child) => {
        const columnKey = child.dataset && child.dataset.column && sizes[child.dataset.column]

        if (columnKey) return tot + sizes[columnKey]
        return tot + child.clientWidth
      }, 0)

      return totalWidth > this.$refs.header.clientWidth
    },

    mountSortable() {
      this.instance = new Sortable(this.$refs.header, {
        handle: ".drag-handle",
        draggable: ".cell",
        animation: 100,
        sort: true,
        filter: ".ignore-element",

        forceFallback: true,
        fallbackOnBody: true,
        direction: "horizontal",

        onUpdate: () => {
          this.$nextTick(() => {
            const items = Array.from(this.$refs.header.children)
            this.$emit("update-column-order", items.map(el => el.dataset.column))
          })
        },
      })
    },
    destroySortableInstance() {
      this.instance && this.instance.destroy()
      this.instance = null
    },
    removeLastColumnCustomSize(updateModel = true) {
      const variables = this.columnVariables
      const lastVariable = variables[variables.length - 1]
      const newSizesWithoutLastVariable = {...this.sizes}

      delete newSizesWithoutLastVariable[lastVariable]

      this.sizes = newSizesWithoutLastVariable
      updateModel && this.$emit("input", this.sizes)
    },
  },
  computed: {
    mouseWidth() {
      return this.mouseX - this.initialX
    },
    newColumnSize() {
      if (!this.columnResize) return;
      const width = this.initialClientWidth + this.mouseWidth
      if (width < 50) return 50
      return width
    },

    enableSortable() {
      return this.columns && this.columns.length > 1
    },
  },
  watch: {
    value(sizes) {
      this.sizes = sizes
    },
    columnVariables() {
      this.removeLastColumnCustomSize()
    },
    newColumnSize(size) {
      if (size && this.columnResize) {
        const newSizes = {
          ...this.sizes,
          [this.columnResize]: size,
        }

        this.sizes = newSizes
        /* if (!this.isOverflowing(newSizes)) {
        } */
      }
    },

    enableSortable(val) {
      this.destroySortableInstance()
      val && this.mountSortable()
    },
  },
}

</script>

<style>

.sortable-fallback {
  background-color: white;
  opacity: 1 !important;
  box-shadow: 0 6px 8px 0 rgba(0,0,0, .1);
  width: 100%;
  overflow: visible;
}

.sortable-ghost {
  background-color: #f6f7f8 !important;
  border-radius: 1px;
}

.sortable-ghost > div,
.sortable-ghost > svg,
.sortable-ghost > input,
.sortable-ghost .drag-handle {
  display: none !important;
}

.first {
  border-left: none !important;
}

</style>

<style scoped>

th {
  border-left: 1px solid rgba(200, 200, 200, .4);
  background-color: white !important;
}


.header {
  position: relative;
  padding-bottom: 0px;
}

.cell {
  background-color: white;
  padding-right: 12px !important;
  flex-basis: 100% !important;
}

thead {
  margin-top: 0px;
}

tr {
  display: flex;
  padding: 0px;
}

th {
  position: relative;
  display: block !important;
  position: relative;
  flex-basis: 100%;
  padding: 10px 0 !important;
  padding-left: 12px !important;
  font-size: 14px;
}

.bordered {
  position: absolute;
  right: 0px;
  top: 0px;
  margin-right: -1px;
  height: 100%;
  width: 5px;
  z-index: 10;
  cursor: col-resize;
  background-color: rgba(0, 0, 0, 0.099);
  opacity: 0;
  transform: translateX(0px) scaleY(1);
  transition-duration: .075s;
}

.bordered:hover {
  opacity: 1;
  width: 8px;
  transform: translateX(3px) scaleY(1.3);
}

</style>
